'use client';

import { useState } from 'react';

import Image from 'next/image';

import { motion } from 'framer-motion';
import { Clock, PlayCircle } from 'lucide-react';

import { CourseUnlockModal } from '@/components/course-unlock-modal';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { COURSE_CATEGORY, getCourseBySlug } from '@/data/courses';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { BASE_URL } from '@/lib/metadata-helper';

interface CourseDetailContentProps {
  slug: string;
}

export function CourseDetailContent({ slug }: CourseDetailContentProps) {
  const { t, locale } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const course = getCourseBySlug(slug, t.academy);

  if (!course) {
    return null;
  }

  // Calculate total lessons
  const totalLessons = course.modules.reduce(
    (acc, module) => acc + module.lessons.length,
    0,
  );

  // The visual breadcrumb below has always been there; the structured data to
  // match it has not, which is why every course page was one of the 66 without
  // a BreadcrumbList. Names come from the same strings the trail renders, so
  // the two cannot disagree.
  const localePrefix = locale === 'ar' ? '/ar' : '';
  const breadcrumb = {
    '@type': 'BreadcrumbList',
    '@id': `${BASE_URL}${localePrefix}/open-academy/courses/${slug}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: locale === 'ar' ? 'الرئيسية' : 'Home',
        item: `${BASE_URL}${localePrefix}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t.academy['Open Academy'],
        item: `${BASE_URL}${localePrefix}/open-academy`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: course.title,
        item: `${BASE_URL}${localePrefix}/open-academy/courses/${slug}`,
      },
    ],
  };

  // JSON-LD structured data for SEO
  const courseNode = {
    '@type': 'Course',
    name: course.title,
    description: course.longDescription,
    provider: {
      '@type': 'Organization',
      name: 'Open Academy',
      sameAs: 'https://www.open.cx',
    },
    // Google's Course type wants a subject classification; every course here
    // teaches the same subject, so it is a constant rather than a per-course
    // field nobody would keep up to date.
    category: COURSE_CATEGORY,
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: course.duration,
    },
    educationalLevel: course.level,
    image: `${BASE_URL}${course.image}`,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    // `numberOfLessons` is not a schema.org property, so validators dropped it
    // and flagged the item. The same information is carried by the syllabus,
    // which is a real property: one section per module, named as the page
    // names it. No durations are asserted here — the per-lesson times are
    // display strings ("15:30"), not the ISO 8601 this field would need.
    syllabusSections: course.modules.map((module) => ({
      '@type': 'Syllabus',
      name: module.title,
    })),
    coursePrerequisites:
      course.level === t.academy.Beginner
        ? locale === 'ar'
          ? 'ما في متطلبات سابقة'
          : 'None'
        : locale === 'ar'
          ? 'يُفضَّل الإلمام بالأساسيات'
          : 'Basic understanding recommended',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [courseNode, breadcrumb],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <CourseUnlockModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        courseTitle={course.title}
        courseImage={course.image}
      />
      <div className="bg-background text-foreground min-h-screen pb-24 pt-32">
        <div className="container max-w-7xl">
          {/* Breadcrumb - Fade in */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="mb-8"
          >
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <Link href="/">{t.academy.Home}</Link>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <Link href="/open-academy">{t.academy['Open Academy']}</Link>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <Link href="/open-academy#courses">{t.academy.Courses}</Link>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{course.title}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </motion.div>

          {/* Header / Hero Area - Scale and fade in */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            className="relative mb-12 overflow-hidden rounded-2xl border bg-black text-white shadow-2xl"
          >
            <div className="relative aspect-video w-full lg:h-[500px]">
              <Image
                src={course.image}
                alt={course.title}
                fill
                className="object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-12">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="max-w-3xl space-y-4"
                >
                  <Badge
                    variant="secondary"
                    className="bg-white/20 text-white backdrop-blur-sm hover:bg-white/30"
                  >
                    {course.level}
                  </Badge>
                  <h1 className="font-serif text-4xl lg:text-6xl">
                    {course.title}
                  </h1>
                  <p className="text-lg text-neutral-200 lg:text-xl">
                    {course.description}
                  </p>
                  <div className="flex items-center gap-4 pt-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-neutral-300" />
                      <span className="font-medium text-white">
                        {course.duration}
                      </span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          <div className="grid gap-12 lg:grid-cols-[1fr_350px]">
            {/* Main Content */}
            <div>
              {/* Description - Fade in */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
                className="mb-12"
              >
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {course.longDescription}
                </p>
              </motion.div>

              {/* Modules - Staggered fade in */}
              <div className="space-y-8">
                {course.modules.map((module, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.5 + i * 0.1,
                      ease: 'easeOut',
                    }}
                  >
                    <h3 className="mb-4 text-2xl">{module.title}</h3>
                    <div className="space-y-2">
                      {module.lessons.map((lesson, j) => (
                        <motion.div
                          key={j}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            duration: 0.4,
                            delay: 0.6 + i * 0.1 + j * 0.05,
                            ease: 'easeOut',
                          }}
                          onClick={() => setIsModalOpen(true)}
                          className="bg-card hover:bg-accent hover:text-accent-foreground group flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <PlayCircle className="text-muted-foreground group-hover:text-primary h-5 w-5 transition-colors" />
                            <span className="font-medium">{lesson.title}</span>
                          </div>
                          <span className="text-muted-foreground text-sm">
                            {lesson.duration}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Sidebar - Fade in from right */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: 'easeOut' }}
              className="space-y-6"
            >
              <Card className="bg-card">
                <CardHeader>
                  <CardTitle>{t.academy['Course details']}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Clock className="text-muted-foreground h-5 w-5" />
                    <span className="font-medium">{course.duration}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{course.level}</span>
                  </div>
                  <div className="pt-4">
                    <Button
                      className="w-full"
                      onClick={() => setIsModalOpen(true)}
                    >
                      {t.academy['Join Waitlist']}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
